import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppLayout from './components/AppLayout';

const LoginPage              = lazy(() => import('./pages/LoginPage'));
const ResetPasswordPage      = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage          = lazy(() => import('./pages/DashboardPage'));

// Accounts
const AccountList   = lazy(() => import('./features/accounts/AccountList'));
const AccountDetail = lazy(() => import('./features/accounts/AccountDetail'));
const AccountForm   = lazy(() => import('./features/accounts/AccountForm'));

// Contacts
const ContactList   = lazy(() => import('./features/contacts/ContactList'));
const ContactDetail = lazy(() => import('./features/contacts/ContactDetail'));
const ContactForm   = lazy(() => import('./features/contacts/ContactForm'));

// Call Logs
const CallLogList   = lazy(() => import('./features/call-logs/CallLogList'));
const CallLogDetail = lazy(() => import('./features/call-logs/CallLogDetail'));
const FullLogForm   = lazy(() => import('./features/call-logs/FullLogForm'));

// Deals
const DealList   = lazy(() => import('./features/deals/DealList'));
const DealDetail = lazy(() => import('./features/deals/DealDetail'));
const DealForm   = lazy(() => import('./features/deals/DealForm'));

// Tasks
const TasksPage = lazy(() => import('./pages/TasksPage'));

// Security / Profile
const SecurityPage = lazy(() => import('./pages/SecurityPage'));

// Integrations
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'));

// Emails
const EmailsPage = lazy(() => import('./pages/EmailsPage'));

// Reports
const ReportsPage = lazy(() => import('./pages/ReportsPage'));

// Quotes
const QuoteApprovalsPage = lazy(() => import('./pages/QuoteApprovalsPage'));

// Public (no auth)
const ProposalPublicPage = lazy(() => import('./pages/ProposalPublicPage'));
const ContractPublicPage = lazy(() => import('./pages/ContractPublicPage'));

// Contracts
const ContractsList           = lazy(() => import('./features/contracts/ContractsList'));
const ContractEditor          = lazy(() => import('./features/contracts/ContractEditor'));
const PendingCountersignPage  = lazy(() => import('./features/contracts/PendingCountersignPage'));

// Admin
const AdminUsersPage              = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminRbacPage               = lazy(() => import('./pages/admin/AdminRbacPage'));
const LoginEventsPage             = lazy(() => import('./pages/admin/LoginEventsPage'));
const AdminTeamsPage              = lazy(() => import('./pages/admin/AdminTeamsPage'));
const AdminMsaPricingPage         = lazy(() => import('./pages/admin/AdminMsaPricingPage'));
const AdminProposalTemplatesPage  = lazy(() => import('./pages/admin/AdminProposalTemplatesPage'));
const AdminContractTemplatesPage  = lazy(() => import('./pages/admin/AdminContractTemplatesPage'));
const AdminMediaPage              = lazy(() => import('./pages/admin/AdminMediaPage'));

// Hooks (for edit wrappers)
import { useAccount } from './hooks/useAccounts';
import { useContact } from './hooks/useContacts';
import { useDeal }    from './hooks/useDeals';
import { useCallLog } from './hooks/useCallLogs';

// Edit wrappers — load existing record then render the form pre-filled
function AccountFormEdit() {
  const { id } = useParams();
  const { data, isLoading } = useAccount(id);
  if (isLoading) return null;
  return <AccountForm account={data} />;
}

function ContactFormEdit() {
  const { id } = useParams();
  const { data, isLoading } = useContact(id);
  if (isLoading) return null;
  return <ContactForm contact={data} />;
}

function DealFormEdit() {
  const { id } = useParams();
  const { data, isLoading } = useDeal(id);
  if (isLoading) return null;
  return <DealForm deal={data} />;
}

function CallLogFormEdit() {
  const { id } = useParams();
  const { data, isLoading } = useCallLog(id);
  if (isLoading) return null;
  return <FullLogForm callLog={data} />;
}

function ComingSoon({ label }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center">
      <p className="text-lg font-semibold text-gray-700">{label}</p>
      <p className="text-sm text-gray-400 mt-1">Coming in a future phase</p>
    </div>
  );
}

function AppRoutes() {
  return (
    <AppLayout>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/"                    element={<DashboardPage />} />

          {/* Accounts */}
          <Route path="/accounts"            element={<AccountList />} />
          <Route path="/accounts/new"        element={<AccountForm />} />
          <Route path="/accounts/:id"        element={<AccountDetail />} />
          <Route path="/accounts/:id/edit"   element={<AccountFormEdit />} />

          {/* Contacts */}
          <Route path="/contacts"            element={<ContactList />} />
          <Route path="/contacts/new"        element={<ContactForm />} />
          <Route path="/contacts/:id"        element={<ContactDetail />} />
          <Route path="/contacts/:id/edit"   element={<ContactFormEdit />} />

          {/* Deals */}
          <Route path="/deals"               element={<DealList />} />
          <Route path="/deals/new"           element={<DealForm />} />
          <Route path="/deals/:id"           element={<DealDetail />} />
          <Route path="/deals/:id/edit"      element={<DealFormEdit />} />

          {/* Call Logs */}
          <Route path="/call-logs"           element={<CallLogList />} />
          <Route path="/call-logs/new"       element={<FullLogForm />} />
          <Route path="/call-logs/:id"       element={<CallLogDetail />} />
          <Route path="/call-logs/:id/edit"  element={<CallLogFormEdit />} />

          {/* Tasks */}
          <Route path="/tasks"               element={<TasksPage />} />

          {/* Security / Profile */}
          <Route path="/profile/security"    element={<SecurityPage />} />

          {/* Emails */}
          <Route path="/emails"              element={<EmailsPage />} />

          {/* Reports */}
          <Route path="/reports"             element={<ReportsPage />} />

          {/* Integrations */}
          <Route path="/settings/integrations" element={<IntegrationsPage />} />

          {/* Quotes */}
          <Route path="/quotes/approvals"    element={<QuoteApprovalsPage />} />

          {/* Contracts */}
          <Route path="/contracts"           element={<ContractsList />} />
          <Route path="/contracts/pending"   element={<PendingCountersignPage />} />
          <Route path="/contracts/:id"       element={<ContractEditor />} />

          {/* Admin */}
          <Route path="/admin/users"                  element={<AdminUsersPage />} />
          <Route path="/admin/teams"                  element={<AdminTeamsPage />} />
          <Route path="/admin/msa-pricing"            element={<AdminMsaPricingPage />} />
          <Route path="/admin/proposal-templates"     element={<AdminProposalTemplatesPage />} />
          <Route path="/admin/contract-templates"     element={<AdminContractTemplatesPage />} />
          <Route path="/admin/media"                  element={<AdminMediaPage />} />
          <Route path="/admin/rbac"                   element={<AdminRbacPage />} />
          <Route path="/admin/login-events"           element={<LoginEventsPage />} />

          <Route path="*"                    element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/login"          element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/p/:token"               element={<ProposalPublicPage />} />
          <Route path="/contracts/sign/:token"  element={<ContractPublicPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppRoutes />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </AuthProvider>
  );
}
